/**
 * Supervision / indirects plan — Foreman + General Foreman from the staffing curve.
 * Supervision is PER TRADE (P6 resource): FM = ceil(men/8) within the trade;
 * GF = floor rule, first GF at 24 men in ONE trade (8 × 3), next at 48.
 * "All trades" = weekly sum of per-trade needs. Export xlsx.
 */
import React, { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  buildSupervisionPlan,
  ratioToPercent,
  percentToRatio,
  DEFAULT_MEN_PER_FM,
  DEFAULT_FM_PER_GF
} from '../../utils/staffingPlan';
import { exportFilename } from '../../utils/nomenclature';
import { resourceFilterLabel } from '../../utils/evmLegend';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { authenticatedApiCall } from '../../utils/apiClient';
import { addHorizontalPdfTables, addHorizontalWorksheet } from '../../utils/planExport';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FM = '#d97706';
const GF = '#6d28d9';

function formatWeek(ymd) {
  if (!ymd) return { tip: '' };
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return { tip: `${d} ${MONTHS[(m || 1) - 1]} ${y}` };
}

function neededWhen(kind, row) {
  if (row.basis === 'count') {
    return `Trade needs sum to ${row.threshold}+ ${kind === 'fm' ? 'FM' : 'GF'}${row.threshold === 1 ? '' : 's'}`;
  }
  if (kind === 'fm') {
    return row.threshold === 0 ? 'Any crew in this trade' : `Trade crew above ${row.threshold} men`;
  }
  // GF floor rule: first GF only at N men within the one trade
  return `${row.threshold}+ men in this trade`;
}

const th = (align = 'left') => ({
  textAlign: align,
  padding: '0.3rem 0.45rem',
  borderBottom: '2px solid #e2e8f0',
  fontSize: '0.66rem',
  color: '#64748b',
  letterSpacing: '0.03em',
  textTransform: 'uppercase'
});

const td = { padding: '0.32rem 0.45rem', fontSize: '0.78rem', color: '#334155' };
const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const inputStyle = {
  width: 48, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px'
};

function WindowsTable({ rows, kind, color }) {
  if (!rows?.length) {
    return <div style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '0.4rem 0' }}>None in this window.</div>;
  }
  const title = kind === 'fm' ? 'Foreman' : 'General foreman';
  const peakHead = rows[0]?.basis === 'count'
    ? (kind === 'fm' ? 'Peak FM' : 'Peak GF')
    : 'Peak crew';
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', marginTop: 4 }}>
      <thead>
        <tr>
          {[title, 'Needed when', 'From', 'To', 'Weeks', peakHead].map((h) => (
            <th key={h} style={th(h === title || h === 'Needed when' ? 'left' : 'right')}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${kind}-${r.n}`} style={{ background: r.n % 2 === 0 ? '#f8fafc' : 'white' }}>
            <td style={{ ...td, fontWeight: 800, color, whiteSpace: 'nowrap' }}>
              {kind === 'fm' ? `FM #${r.n}` : `GF #${r.n}`}
            </td>
            <td style={td}>{neededWhen(kind, r)}</td>
            <td style={num}>{formatWeek(r.from).tip}</td>
            <td style={num}>{formatWeek(r.to).tip}</td>
            <td style={num}>{r.weeks}</td>
            <td style={num}>{r.peak}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Shared note lines for the xlsx Notes sheet and the PDF footer. */
function planNoteLines(plan, jobNumber) {
  return [
    `Job ${jobNumber || ''}`,
    'Supervision is PER TRADE (P6 resource ID) — never one ratio on the whole job.',
    `FM: 1 per ${plan.menPerFm} men within the trade (${plan.menPerFm} men = 1 FM, ${plan.menPerFm + 1} = 2 — same ceil as Safety).`,
    `GF: floor rule — no GF under ${plan.gfMenPer} men in a trade; the first GF starts at ${plan.gfMenPer} men in ONE trade, the next at ${plan.gfMenPer * 2}.`,
    '"All trades (sum)" rows are the week-by-week sum of each trade\'s own FM/GF needs; a GF is never shared across trades.',
    'Base: direct crew only — FMs and GFs are the indirects, computed from the direct men (P6 indirect IDs are skipped).',
    'Crew = Staffing tab curve (earned to the data date, then forecast, hiring overrides applied).'
  ];
}

async function exportPlanXlsx(plan, { jobNumber }) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LGL Portal';
  const fmt = (ymd) => formatWeek(ymd).tip;

  const summary = wb.addWorksheet('Summary');
  summary.columns = [
    { header: 'Scope', key: 'scope', width: 28 },
    { header: 'Peak men', key: 'peakMen', width: 12 },
    { header: 'Peak FM', key: 'peakFm', width: 12 },
    { header: 'Peak GF', key: 'peakGf', width: 12 },
    { header: '1 FM per N men', key: 'menPerFm', width: 16 },
    { header: '1 GF per N FM', key: 'fmPerGf', width: 14 },
    { header: 'First GF at (men)', key: 'gfMenPer', width: 16 },
    { header: 'Override', key: 'overridden', width: 12 }
  ];
  summary.addRow({
    scope: 'All trades (sum)',
    peakMen: plan.overall.peakMen,
    peakFm: plan.overall.peakFm,
    peakGf: plan.overall.peakGf,
    menPerFm: plan.menPerFm,
    fmPerGf: plan.fmPerGf,
    gfMenPer: plan.gfMenPer,
    overridden: ''
  });
  plan.byResource.forEach((r) => {
    summary.addRow({
      scope: r.label,
      peakMen: r.peakMen,
      peakFm: r.peakFm,
      peakGf: r.peakGf,
      menPerFm: r.menPerFm,
      fmPerGf: r.fmPerGf,
      gfMenPer: r.gfMenPer,
      overridden: r.overridden ? 'yes' : ''
    });
  });
  summary.addRow({
    scope: 'Sum of per-resource peaks',
    peakMen: '',
    peakFm: plan.sumPeakFm,
    peakGf: plan.sumPeakGf
  });
  styleHeader(summary);

  const weekly = wb.addWorksheet('Weekly');
  weekly.columns = [
    { header: 'Scope', key: 'scope', width: 28 },
    { header: 'Week starting', key: 'week', width: 16 },
    { header: 'Week', key: 'weekTip', width: 16 },
    { header: 'Men', key: 'men', width: 10 },
    { header: 'FM needed', key: 'fm', width: 12 },
    { header: 'GF needed', key: 'gf', width: 12 }
  ];
  plan.weeks.forEach((w, i) => {
    weekly.addRow({
      scope: 'All trades (sum)', week: w, weekTip: fmt(w),
      men: plan.overall.men[i], fm: plan.overall.fm[i], gf: plan.overall.gf[i]
    });
  });
  plan.byResource.forEach((r) => {
    plan.weeks.forEach((w, i) => {
      weekly.addRow({
        scope: r.label, week: w, weekTip: fmt(w),
        men: r.men[i], fm: r.fm[i], gf: r.gf[i]
      });
    });
  });
  styleHeader(weekly);

  const byWeekRows = [
    { label: 'All trades — Men', values: plan.overall.men },
    { label: 'All trades — FM', values: plan.overall.fm },
    { label: 'All trades — GF', values: plan.overall.gf },
    ...plan.byResource.flatMap((r) => ([
      { label: `${r.label} — Men`, values: r.men },
      { label: `${r.label} — FM`, values: r.fm },
      { label: `${r.label} — GF`, values: r.gf }
    ]))
  ];
  addHorizontalWorksheet(wb, {
    name: 'By week',
    rowHeader: 'Scope',
    columns: plan.weeks.map((w) => fmt(w)),
    rows: byWeekRows,
    argb: 'FFD97706'
  });

  const windows = wb.addWorksheet('Windows');
  windows.columns = [
    { header: 'Scope', key: 'scope', width: 28 },
    { header: 'Role', key: 'role', width: 10 },
    { header: '#', key: 'n', width: 8 },
    { header: 'Needed when', key: 'when', width: 22 },
    { header: 'From', key: 'from', width: 16 },
    { header: 'To', key: 'to', width: 16 },
    { header: 'Weeks', key: 'weeks', width: 10 },
    { header: 'Peak', key: 'peak', width: 10 }
  ];
  const pushWindows = (scope, kind, rows) => {
    rows.forEach((r) => {
      windows.addRow({
        scope,
        role: kind === 'fm' ? 'Foreman' : 'GF',
        n: r.n,
        when: neededWhen(kind, r),
        from: fmt(r.from),
        to: fmt(r.to),
        weeks: r.weeks,
        peak: r.peak
      });
    });
  };
  pushWindows('All trades (sum)', 'fm', plan.overall.fmWindows);
  pushWindows('All trades (sum)', 'gf', plan.overall.gfWindows);
  plan.byResource.forEach((r) => {
    pushWindows(r.label, 'fm', r.fmWindows);
    pushWindows(r.label, 'gf', r.gfWindows);
  });
  styleHeader(windows);

  const notes = wb.addWorksheet('Notes');
  notes.getColumn(1).width = 90;
  planNoteLines(plan, jobNumber).forEach((line, i) => { notes.getCell(i + 1, 1).value = line; });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = exportFilename({ job: jobNumber, reportSlug: 'supervision-plan', ext: 'xlsx' });
  a.click();
  URL.revokeObjectURL(url);
}

/** Letterheaded portrait PDF: summary + FM/GF windows + rule notes. */
async function buildPlanPdf(plan, { jobNumber, instance, accounts }) {
  const [{ default: jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);
  const autoTable = autoTableMod.default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber, margin: 14 });
  const fmt = (ymd) => formatWeek(ymd).tip;

  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text('Supervision plan — foremen & general foremen', 14, letterheadY);
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Per trade (P6 resource): 1 FM per ${plan.menPerFm} men; first GF at ${plan.gfMenPer} men in one trade, next at ${plan.gfMenPer * 2}. Generated ${new Date().toLocaleString()}.`,
    14,
    letterheadY + 5
  );

  autoTable(doc, {
    startY: letterheadY + 9,
    head: [['Scope', 'Peak men', 'Peak FM', 'Peak GF', '1 FM per', '1 GF per', 'First GF at']],
    body: [
      ['All trades (sum)', plan.overall.peakMen, plan.overall.peakFm, plan.overall.peakGf, plan.menPerFm, plan.fmPerGf, plan.gfMenPer],
      ...plan.byResource.map((r) => [
        `${r.label}${r.overridden ? ' *' : ''}`, r.peakMen, r.peakFm, r.peakGf, r.menPerFm, r.fmPerGf, r.gfMenPer
      ])
    ],
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles: { fillColor: [15, 118, 110], fontSize: 8 },
    alternateRowStyles: { fillColor: [248, 250, 252] }
  });

  const windowRows = [];
  const pushWin = (scope, kind, rows) => (rows || []).forEach((r) => {
    windowRows.push([
      scope,
      kind === 'fm' ? 'Foreman' : 'GF',
      r.n,
      neededWhen(kind, r),
      fmt(r.from),
      fmt(r.to),
      r.weeks,
      r.peak
    ]);
  });
  pushWin('All trades (sum)', 'fm', plan.overall.fmWindows);
  pushWin('All trades (sum)', 'gf', plan.overall.gfWindows);
  plan.byResource.forEach((r) => {
    pushWin(r.label, 'fm', r.fmWindows);
    pushWin(r.label, 'gf', r.gfWindows);
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 6,
    head: [['Scope', 'Role', '#', 'Needed when', 'From', 'To', 'Weeks', 'Peak']],
    body: windowRows,
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles: { fillColor: [217, 119, 6], fontSize: 8 },
    alternateRowStyles: { fillColor: [255, 251, 235] }
  });

  const byWeekRows = [
    { label: 'All trades — Men', values: plan.overall.men },
    { label: 'All trades — FM', values: plan.overall.fm },
    { label: 'All trades — GF', values: plan.overall.gf }
  ];
  if (byWeekRows[0].values.length) {
    doc.addPage('letter', 'landscape');
    addHorizontalPdfTables(doc, autoTable, {
      title: 'Supervision by week',
      columns: plan.weeks.map((w) => fmt(w)),
      rows: byWeekRows,
      startY: 16,
      headColor: [217, 119, 6]
    });
  }

  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  let y = doc.lastAutoTable.finalY + 6;
  const pageH = doc.internal.pageSize.getHeight();
  planNoteLines(plan, jobNumber).forEach((line) => {
    if (y > pageH - 12) { doc.addPage(); y = 14; }
    doc.text(line, 14, y, { maxWidth: doc.internal.pageSize.getWidth() - 28 });
    y += 4.5;
  });
  return doc;
}

function styleHeader(ws) {
  const header = ws.getRow(1);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
  header.alignment = { vertical: 'middle' };
  ws.views = [{ state: 'frozen', ySplit: 1 }];
}

export default function SupervisionPlanModal({
  open,
  onClose,
  weeks,
  resources,
  menByResource,
  jobNumber,
  menPerFm = DEFAULT_MEN_PER_FM,
  fmPerGf = DEFAULT_FM_PER_GF,
  onMenPerFm,
  onFmPerGf,
  cfg,
  onPatchCfg
}) {
  const { instance, accounts } = useMsal();
  const [tab, setTab] = useState('overall');
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(null); // 'xlsx' | 'pdf' | 'email' | null
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMsg, setEmailMsg] = useState('');

  const plan = useMemo(() => buildSupervisionPlan({
    weeks,
    resources,
    menByResource,
    from: cfg?.from || '',
    to: cfg?.to || '',
    menPerFm,
    fmPerGf,
    overrides: cfg?.overrides || {}
  }), [weeks, resources, menByResource, cfg, menPerFm, fmPerGf]);

  if (!open) return null;

  const patch = (p) => onPatchCfg?.(p);
  const setOverride = (resourceId, patchOv) => {
    const next = { ...(cfg?.overrides || {}) };
    const cur = { ...(next[resourceId] || {}), ...patchOv };
    if (cur.menPerFm == null && cur.fmPerGf == null) delete next[resourceId];
    else next[resourceId] = cur;
    patch({ overrides: next });
  };

  const onExportXlsx = async () => {
    setExporting('xlsx');
    try {
      await exportPlanXlsx(plan, { jobNumber });
    } catch (err) {
      console.error('Supervision xlsx export failed:', err);
      alert(`xlsx export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  const onExportPdf = async () => {
    setExporting('pdf');
    try {
      const doc = await buildPlanPdf(plan, { jobNumber, instance, accounts });
      doc.save(exportFilename({ job: jobNumber, reportSlug: 'supervision-plan', ext: 'pdf' }));
    } catch (err) {
      console.error('Supervision PDF export failed:', err);
      alert(`PDF export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  const onSendEmail = async () => {
    const recipients = emailTo.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      alert('Enter at least one recipient email.');
      return;
    }
    setExporting('email');
    try {
      const doc = await buildPlanPdf(plan, { jobNumber, instance, accounts });
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const subject = emailSubject.trim() || `Supervision plan — Job ${jobNumber || ''}`.trim();
      await authenticatedApiCall(instance, accounts, 'post', '/email/send', {
        to: recipients,
        subject,
        htmlBody: `<p>${emailMsg ? emailMsg.replace(/\n/g, '<br/>') : `Attached: Supervision plan (foremen / general foremen) for job ${jobNumber || ''}.`}</p>
          <p style="color:#64748b;font-size:12px;">Per trade (P6 resource): 1 FM per ${plan.menPerFm} men; first GF at ${plan.gfMenPer} men in one trade. Generated ${new Date().toLocaleString()}.</p>`,
        attachments: [{
          filename: exportFilename({ job: jobNumber, reportSlug: 'supervision-plan', ext: 'pdf' }),
          fileblob: pdfBase64
        }],
        feature: 'generic'
      }, { timeout: 120000 });
      setEmailOpen(false);
      setEmailMsg('');
      alert('Supervision plan emailed.');
    } catch (err) {
      console.error('Supervision plan email failed:', err);
      const detail = err.response?.data?.error || err.response?.data?.message || err.message || '';
      alert(`Failed to send email${detail ? `: ${detail}` : ''}`);
    } finally {
      setExporting(null);
    }
  };

  const pct = ratioToPercent(menPerFm);
  const exportBtn = (label, onClick, { solid = false } = {}) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!!exporting}
      style={{
        border: '1px solid #0f766e', background: solid ? '#0f766e' : 'white',
        color: solid ? 'white' : '#0f766e',
        borderRadius: 7, fontSize: '0.75rem', fontWeight: 800, padding: '0.35rem 0.7rem',
        cursor: exporting ? 'wait' : 'pointer', whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Supervision staffing plan"
        style={{
          background: 'white', borderRadius: 12, width: 'min(920px, 96vw)', maxHeight: '88vh',
          overflow: 'auto', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '0.85rem 1rem',
          borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'white', zIndex: 1
        }}>
          <div>
            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>Supervision plan</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
              Estimate foremen and general foremen from the live crew (earned to the data date, then remaining).
              Stays on this page — Supervision is <strong>per trade</strong> (P6 resource): 1 FM per {menPerFm} men within the trade;
              no GF under {menPerFm * fmPerGf} men — the first GF starts at {menPerFm * fmPerGf} men in one trade, the next at {menPerFm * fmPerGf * 2}.
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {exportBtn(exporting === 'xlsx' ? 'Exporting…' : '⬇ xlsx', onExportXlsx, { solid: true })}
            {exportBtn(exporting === 'pdf' ? 'Exporting…' : '⬇ PDF', onExportPdf)}
            {exportBtn(exporting === 'email' ? 'Sending…' : '📧 Email', () => setEmailOpen((v) => !v))}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', fontSize: 20, color: '#64748b', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {emailOpen && (
          <div style={{
            padding: '0.7rem 1rem', borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
            display: 'flex', flexDirection: 'column', gap: 6
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#0f172a' }}>Email the plan (PDF attached)</div>
            <input
              type="text"
              placeholder="Recipients — comma separated emails"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              style={{ fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 8px' }}
            />
            <input
              type="text"
              placeholder={`Subject — default: Supervision plan — Job ${jobNumber || ''}`}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              style={{ fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 8px' }}
            />
            <textarea
              placeholder="Optional message"
              value={emailMsg}
              onChange={(e) => setEmailMsg(e.target.value)}
              rows={2}
              style={{ fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 6, padding: '5px 8px', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onSendEmail}
                disabled={!!exporting}
                style={{
                  border: '1px solid #0f766e', background: '#0f766e', color: 'white', borderRadius: 6,
                  fontSize: '0.75rem', fontWeight: 800, padding: '0.3rem 0.8rem',
                  cursor: exporting ? 'wait' : 'pointer'
                }}
              >
                {exporting === 'email' ? 'Sending…' : 'Send'}
              </button>
              <button
                type="button"
                onClick={() => setEmailOpen(false)}
                style={{
                  border: '1px solid #cbd5e1', background: 'white', color: '#334155', borderRadius: 6,
                  fontSize: '0.75rem', fontWeight: 700, padding: '0.3rem 0.8rem', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div style={{
          padding: '0.7rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.55rem 1.1rem',
          alignItems: 'center', borderBottom: '1px solid #f1f5f9'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>
            1 FM per
            <input
              type="number"
              min={1}
              value={menPerFm}
              onChange={(e) => onMenPerFm?.(e.target.value)}
              style={inputStyle}
            />
            men
            <span style={{ fontWeight: 500, color: '#64748b' }}>({pct}% of crew)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#334155' }}>
            or
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={pct}
              onChange={(e) => onMenPerFm?.(percentToRatio(e.target.value, menPerFm))}
              style={{ ...inputStyle, width: 56 }}
            />
            %
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>
            1 GF per
            <input
              type="number"
              min={1}
              value={fmPerGf}
              onChange={(e) => onFmPerGf?.(e.target.value)}
              style={inputStyle}
            />
            FMs
            <span style={{ fontWeight: 500, color: '#64748b' }}>
              (first GF at {menPerFm * fmPerGf} men in a trade)
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#334155' }}>
            From
            <input
              type="date"
              value={cfg?.from || ''}
              onChange={(e) => patch({ from: e.target.value })}
              style={{ fontSize: '0.72rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '2px 5px' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#334155' }}>
            To
            <input
              type="date"
              value={cfg?.to || ''}
              onChange={(e) => patch({ to: e.target.value })}
              style={{ fontSize: '0.72rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '2px 5px' }}
            />
          </label>
          {(cfg?.from || cfg?.to) && (
            <button
              type="button"
              onClick={() => patch({ from: '', to: '' })}
              style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 5, fontSize: '0.7rem', padding: '0.2rem 0.5rem', cursor: 'pointer', color: '#334155' }}
            >
              Whole schedule
            </button>
          )}
        </div>

        <div style={{ padding: '0.55rem 1rem 0', display: 'flex', gap: 6 }}>
          {[['overall', 'All trades (sum)'], ['resource', 'By resource']].map(([id, lbl]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              style={{
                border: 'none', borderBottom: tab === id ? '2px solid #0f766e' : '2px solid transparent',
                background: 'transparent', padding: '0.35rem 0.55rem', fontSize: '0.8rem', fontWeight: 800,
                color: tab === id ? '#0f766e' : '#64748b', cursor: 'pointer'
              }}
            >
              {lbl}
            </button>
          ))}
        </div>

        <div style={{ padding: '0.7rem 1rem 1.1rem' }}>
          {tab === 'overall' ? (
            plan.overall.peakMen <= 0 ? (
              <div style={{ fontSize: '0.82rem', color: '#64748b', padding: '1rem 0' }}>
                No crew in this window — clear the dates or widen the filters.
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 700, marginBottom: 8 }}>
                  Peak {plan.overall.peakMen} men → up to {plan.overall.peakFm} FM
                  {plan.overall.peakFm === 1 ? '' : 's'} · {plan.overall.peakGf} GF
                  {plan.overall.peakGf === 1 ? '' : 's'} on site at once
                  {' (direct crew)'}
                </div>
                <WindowsTable rows={plan.overall.fmWindows} kind="fm" color={FM} />
                <div style={{ height: 12 }} />
                <WindowsTable rows={plan.overall.gfWindows} kind="gf" color={GF} />
                <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 10, lineHeight: 1.4 }}>
                  Sum of per-trade needs, week by week — supervision is per trade, never one ratio on the whole job.
                  FM: {menPerFm} men in a trade = 1, {menPerFm + 1} = 2 (same ceil as Safety).
                  GF: none under {menPerFm * fmPerGf} men in a trade; first at {menPerFm * fmPerGf}, next at {menPerFm * fmPerGf * 2}.
                  A GF is never shared across trades. Open By resource for each trade's own windows.
                </div>
              </>
            )
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    {['Resource', 'Peak men', 'Peak FM', 'Peak GF', '1 FM per', '1 GF per'].map((h) => (
                      <th key={h} style={th(h === 'Resource' ? 'left' : 'right')}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plan.byResource.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ ...td, color: '#94a3b8' }}>No direct resources in this window.</td>
                    </tr>
                  ) : plan.byResource.map((r) => {
                    const open = openId === r.resourceId;
                    return (
                      <React.Fragment key={r.resourceId}>
                        <tr
                          onClick={() => setOpenId(open ? null : r.resourceId)}
                          style={{
                            background: open ? '#fffbeb' : 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <td style={{ ...td, fontWeight: 700 }}>
                            <span style={{ color: '#94a3b8', marginRight: 6 }}>{open ? '▾' : '▸'}</span>
                            {r.label || resourceFilterLabel(r)}
                            {r.overridden ? (
                              <span style={{ marginLeft: 6, fontSize: '0.62rem', color: FM, fontWeight: 800 }}>override</span>
                            ) : null}
                          </td>
                          <td style={num}>{r.peakMen}</td>
                          <td style={{ ...num, fontWeight: 800, color: FM }}>{r.peakFm}</td>
                          <td style={{ ...num, fontWeight: 800, color: GF }}>{r.peakGf}</td>
                          <td style={num} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              min={1}
                              value={r.menPerFm}
                              onChange={(e) => setOverride(r.resourceId, { menPerFm: Math.max(1, Math.round(Number(e.target.value) || menPerFm)) })}
                              style={{ ...inputStyle, width: 44 }}
                            />
                          </td>
                          <td style={num} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              min={1}
                              value={r.fmPerGf}
                              onChange={(e) => setOverride(r.resourceId, { fmPerGf: Math.max(1, Math.round(Number(e.target.value) || fmPerGf)) })}
                              style={{ ...inputStyle, width: 44 }}
                            />
                            {r.overridden && (
                              <button
                                type="button"
                                title="Use the default ratios"
                                onClick={() => {
                                  const next = { ...(cfg?.overrides || {}) };
                                  delete next[r.resourceId];
                                  patch({ overrides: next });
                                }}
                                style={{
                                  marginLeft: 6, border: 'none', background: 'transparent',
                                  color: '#64748b', fontSize: '0.65rem', cursor: 'pointer', fontWeight: 700
                                }}
                              >
                                reset
                              </button>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={6} style={{ padding: '0.4rem 0.6rem 0.8rem', background: '#fffbeb' }}>
                              <WindowsTable rows={r.fmWindows} kind="fm" color={FM} />
                              <div style={{ height: 8 }} />
                              <WindowsTable rows={r.gfWindows} kind="gf" color={GF} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {Object.keys(cfg?.overrides || {}).length > 0 && (
                <button
                  type="button"
                  onClick={() => patch({ overrides: {} })}
                  style={{
                    marginTop: 10, border: '1px solid #cbd5e1', background: '#f8fafc',
                    borderRadius: 5, fontSize: '0.72rem', padding: '0.25rem 0.55rem', cursor: 'pointer', color: '#334155'
                  }}
                >
                  Reset all resource ratios
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
